import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, ArrowRight, Loader2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const interests = [
  { value: "discovery_call", label: "Free discovery call" },
  { value: "6_session", label: "The Awakening · 6 sessions" },
  { value: "10_session", label: "The Rise · 10 sessions" },
  { value: "workshop", label: "Upcoming workshop" },
];

export default function ContactForm() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    interest: "discovery_call",
    message: "",
    isTestimonial: false,
    consent: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const onChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({ ...f, [name]: type === "checkbox" ? checked : value }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;

    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Please share your name and email so Tammy can reach you.");
      return;
    }

    if (form.isTestimonial && !form.message.trim()) {
      toast.error("Please write a few words for your testimonial.");
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/leads`, {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || null,
        interest: form.isTestimonial ? "testimonial" : form.interest,
        message: form.message.trim() || null,
        is_testimonial: form.isTestimonial,
        consent_to_publish: form.isTestimonial ? form.consent : false,
      });
      setSubmitted(true);
      toast.success(
        form.isTestimonial
          ? "Thank you for sharing your story — it means the world."
          : "Thank you, beautiful soul. Tammy will be in touch."
      );
      setForm({
        name: "",
        email: "",
        phone: "",
        interest: "discovery_call",
        message: "",
        isTestimonial: false,
        consent: false,
      });
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        "Something went sideways. Please try again or text 250-784-5104.";
      toast.error(typeof detail === "string" ? detail : "Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="connect"
      data-testid="contact-section"
      className="relative py-24 lg:py-32 bg-cream-soft"
    >
      <div className="mx-auto max-w-7xl px-6 lg:px-10 grid lg:grid-cols-12 gap-12 lg:gap-20 items-start">
        <div className="lg:col-span-5">
          <p className="text-xs tracking-eyebrow uppercase text-sage mb-6">
            Begin Here
          </p>
          <h2
            data-testid="contact-headline"
            className="font-serif-display text-forest text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight"
          >
            Reach out.
            <br />
            <span className="italic">Softly, on your terms.</span>
          </h2>
          <p className="mt-6 text-forest-soft text-lg leading-relaxed max-w-md">
            Share a few details and Tammy will personally respond within
            48&nbsp;hours. No pressure, no scripts — just a kind first
            conversation.
          </p>

          <div className="mt-10 space-y-5">
            <a
              href="tel:+12507845104"
              data-testid="contact-phone-link"
              className="group flex items-center gap-4 text-forest"
            >
              <span className="h-12 w-12 rounded-full bg-white border border-sage/30 flex items-center justify-center group-hover:bg-forest group-hover:text-cream transition-all duration-500">
                <Phone size={16} />
              </span>
              <div>
                <p className="text-[11px] tracking-eyebrow uppercase text-sage">
                  Call or text
                </p>
                <p className="font-serif-display text-2xl">250-784-5104</p>
              </div>
            </a>
            <a
              href="https://www.alignwithtammy.com"
              target="_blank"
              rel="noreferrer"
              data-testid="contact-website-link"
              className="group flex items-center gap-4 text-forest"
            >
              <span className="h-12 w-12 rounded-full bg-white border border-sage/30 flex items-center justify-center group-hover:bg-forest group-hover:text-cream transition-all duration-500">
                <Mail size={16} />
              </span>
              <div>
                <p className="text-[11px] tracking-eyebrow uppercase text-sage">
                  Visit
                </p>
                <p className="font-serif-display text-2xl">
                  alignwithtammy.com
                </p>
              </div>
            </a>
          </div>
        </div>

        <div className="lg:col-span-7">
          <form
            onSubmit={onSubmit}
            data-testid="contact-form"
            className="rounded-[32px] bg-white border border-sage/30 p-8 lg:p-12 aw-shadow-lg"
          >
            {submitted ? (
              <div
                data-testid="contact-form-success"
                className="text-center py-10"
              >
                <p className="text-xs tracking-eyebrow uppercase text-sage">
                  Received with care
                </p>
                <h3 className="mt-4 font-serif-display text-4xl text-forest leading-tight">
                  Thank you.
                </h3>
                <p className="mt-4 text-forest-soft max-w-md mx-auto">
                  Your message is on its way to Tammy. Take a slow breath —
                  you&apos;ve already begun.
                </p>
                <button
                  type="button"
                  onClick={() => setSubmitted(false)}
                  data-testid="contact-form-new"
                  className="aw-btn-ghost mt-8"
                >
                  Send another note
                </button>
              </div>
            ) : (
              <>
                <div
                  data-testid="form-mode-toggle"
                  className="mb-8 inline-flex rounded-full border border-sage/40 p-1 bg-cream-soft"
                >
                  <button
                    type="button"
                    onClick={() =>
                      setForm((f) => ({ ...f, isTestimonial: false }))
                    }
                    data-testid="mode-inquire"
                    className={`rounded-full px-5 py-2 text-[11px] tracking-eyebrow uppercase transition-all duration-400 ${
                      !form.isTestimonial
                        ? "bg-forest text-cream"
                        : "text-forest-soft hover:text-forest"
                    }`}
                  >
                    Book / Inquire
                  </button>
                  <button
                    type="button"
                    onClick={() =>
                      setForm((f) => ({ ...f, isTestimonial: true }))
                    }
                    data-testid="mode-testimonial"
                    className={`rounded-full px-5 py-2 text-[11px] tracking-eyebrow uppercase transition-all duration-400 ${
                      form.isTestimonial
                        ? "bg-forest text-cream"
                        : "text-forest-soft hover:text-forest"
                    }`}
                  >
                    Leave a Testimonial
                  </button>
                </div>

                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label
                      htmlFor="name"
                      className="block text-[11px] tracking-eyebrow uppercase text-sage mb-2"
                    >
                      Your name
                    </label>
                    <input
                      id="name"
                      name="name"
                      type="text"
                      required
                      autoComplete="name"
                      value={form.name}
                      onChange={onChange}
                      data-testid="contact-input-name"
                      className="aw-input w-full"
                      placeholder="Jane Doe"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="email"
                      className="block text-[11px] tracking-eyebrow uppercase text-sage mb-2"
                    >
                      Email
                    </label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      required
                      autoComplete="email"
                      value={form.email}
                      onChange={onChange}
                      data-testid="contact-input-email"
                      className="aw-input w-full"
                      placeholder="you@example.com"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="phone"
                      className="block text-[11px] tracking-eyebrow uppercase text-sage mb-2"
                    >
                      Phone (optional)
                    </label>
                    <input
                      id="phone"
                      name="phone"
                      type="tel"
                      autoComplete="tel"
                      value={form.phone}
                      onChange={onChange}
                      data-testid="contact-input-phone"
                      className="aw-input w-full"
                      placeholder="250-555-0100"
                    />
                  </div>
                  {!form.isTestimonial && (
                    <div>
                      <label
                        htmlFor="interest"
                        className="block text-[11px] tracking-eyebrow uppercase text-sage mb-2"
                      >
                        I&apos;m curious about
                      </label>
                      <select
                        id="interest"
                        name="interest"
                        value={form.interest}
                        onChange={onChange}
                        data-testid="contact-select-interest"
                        className="aw-input w-full pr-10 appearance-none bg-[url('data:image/svg+xml;utf8,<svg xmlns=%22http://www.w3.org/2000/svg%22 width=%2212%22 height=%2212%22 viewBox=%220 0 24 24%22 fill=%22none%22 stroke=%22%232C3B2E%22 stroke-width=%221.5%22 stroke-linecap=%22round%22 stroke-linejoin=%22round%22><polyline points=%226 9 12 15 18 9%22/></svg>')] bg-no-repeat bg-[right_1rem_center]"
                      >
                        {interests.map((i) => (
                          <option key={i.value} value={i.value}>
                            {i.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>

                <div className="mt-5">
                  <label
                    htmlFor="message"
                    className="block text-[11px] tracking-eyebrow uppercase text-sage mb-2"
                  >
                    {form.isTestimonial
                      ? "Your testimonial"
                      : "A few words about where you are (optional)"}
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    rows={4}
                    value={form.message}
                    onChange={onChange}
                    data-testid="contact-textarea-message"
                    className="aw-textarea w-full"
                    placeholder={
                      form.isTestimonial
                        ? "Share how your journey with Align Wellness felt — what shifted, released, or rose within you."
                        : "What feels heavy? What's calling you here?"
                    }
                  />
                </div>

                {form.isTestimonial && (
                  <label
                    htmlFor="consent"
                    data-testid="contact-consent-wrap"
                    className="mt-4 flex items-start gap-3 cursor-pointer rounded-xl border border-sage/30 bg-cream-soft p-4"
                  >
                    <input
                      id="consent"
                      name="consent"
                      type="checkbox"
                      checked={form.consent}
                      onChange={onChange}
                      data-testid="contact-consent-checkbox"
                      className="mt-0.5 h-4 w-4 shrink-0 accent-[#2C3B2E]"
                    />
                    <span className="text-sm text-forest-soft leading-relaxed">
                      I&apos;m happy for Align Wellness to share my words on the
                      website, shown as my first name and last initial.
                    </span>
                  </label>
                )}

                <div className="mt-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <p className="text-xs text-forest-soft max-w-xs">
                    {form.isTestimonial
                      ? "Thank you for sharing. We'll only publish your words if you've ticked the box above."
                      : "By sending, you agree to be contacted by Tammy. Your words are held with care."}
                  </p>
                  <button
                    type="submit"
                    disabled={submitting}
                    data-testid="contact-form-submit"
                    className="aw-btn-primary"
                  >
                    {submitting ? (
                      <>
                        <Loader2 size={16} className="animate-spin" />
                        Sending
                      </>
                    ) : (
                      <>
                        {form.isTestimonial ? "Share my story" : "Send with care"}
                        <ArrowRight size={16} />
                      </>
                    )}
                  </button>
                </div>
              </>
            )}
          </form>
        </div>
      </div>
    </section>
  );
}
